import {
  Box,
  Container,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Link,
  Image,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { FaPhone, FaWhatsapp, FaInstagram, FaMapMarkerAlt, FaEnvelope } from 'react-icons/fa';
import { logoTransparent, contactInfo, practiceAreas, teamMembers } from '../lib/storage';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const founder = teamMembers[0];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Box as="footer" bg="graphite" color="white" pt={16} pb={8}>
      <Container maxW="1400px">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={{ base: 8, md: 12 }} mb={12}>
          <VStack align="flex-start" spacing={6}>
            <Image src={logoTransparent} alt="Mendes Assessoria Jurídica - Logotipo" h="64px" objectFit="contain" />
            <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7">
              Escritório de advocacia fundado em 2011, com atendimento digital em todo o território nacional e sede matriz em Paranavaí - PR.
            </Text>
          </VStack>

          <VStack align="flex-start" spacing={4}>
            <Text fontSize="lg" fontWeight={700} color="white" mb={2}>Links Rápidos</Text>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('home')} cursor="pointer">Início</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('about')} cursor="pointer">Sobre Nós</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('practice-areas')} cursor="pointer">Áreas de Atuação</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('team')} cursor="pointer">Nossa Equipe</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('testimonials')} cursor="pointer">Depoimentos</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('locations')} cursor="pointer">Unidades</Link>
            <Link fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white', textDecoration: 'underline' }} onClick={() => scrollToSection('contact')} cursor="pointer">Contato</Link>
          </VStack>

          <VStack align="flex-start" spacing={4}>
            <Text fontSize="lg" fontWeight={700} color="white" mb={2}>Áreas de Atuação</Text>
            {practiceAreas.map((area) => (
              <Text key={area.id} fontSize="sm" color="whiteAlpha.800" lineHeight="1.5">
                {area.title}
              </Text>
            ))}
          </VStack>

          <VStack align="flex-start" spacing={4}>
            <Text fontSize="lg" fontWeight={700} color="white" mb={2}>Contato</Text>

            <HStack spacing={3} align="flex-start">
              <Icon as={FaPhone} color="stormyTeal" mt={1} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" color="whiteAlpha.600">Central de Atendimento</Text>
                <Link href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} fontSize="sm" color="whiteAlpha.900" _hover={{ color: 'white' }}>{contactInfo.phone}</Link>
              </VStack>
            </HStack>

            <HStack spacing={3} align="flex-start">
              <Icon as={FaWhatsapp} color="stormyTeal" mt={1} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" color="whiteAlpha.600">WhatsApp</Text>
                <Link href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`} isExternal fontSize="sm" color="whiteAlpha.900" _hover={{ color: 'white' }}>{contactInfo.whatsapp}</Link>
              </VStack>
            </HStack>

            <HStack spacing={3} align="flex-start">
              <Icon as={FaInstagram} color="stormyTeal" mt={1} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" color="whiteAlpha.600">Instagram</Text>
                <Link href={`https://instagram.com/${contactInfo.instagram}`} isExternal fontSize="sm" color="whiteAlpha.900" _hover={{ color: 'white' }}>@{contactInfo.instagram}</Link>
              </VStack>
            </HStack>

            <HStack spacing={3} align="flex-start">
              <Icon as={FaEnvelope} color="stormyTeal" mt={1} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" color="whiteAlpha.600">E-mail</Text>
                <Link href={`mailto:${contactInfo.email}`} fontSize="sm" color="whiteAlpha.900" _hover={{ color: 'white' }} maxW="220px">{contactInfo.email}</Link>
              </VStack>
            </HStack>

            <HStack spacing={3} align="flex-start">
              <Icon as={FaMapMarkerAlt} color="stormyTeal" mt={1} />
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="xs" color="whiteAlpha.600">Sede Matriz</Text>
                <Text fontSize="sm" color="whiteAlpha.900" lineHeight="1.5">
                  {contactInfo.address}
                  <br />
                  {contactInfo.cityState}
                  <br />
                  {contactInfo.cep}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </SimpleGrid>

        <Divider borderColor="whiteAlpha.300" my={8} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} alignItems="center">
          <Text fontSize="sm" color="whiteAlpha.700" textAlign={{ base: 'center', md: 'left' }}>
            © {currentYear} Mendes Assessoria Jurídica. Todos os direitos reservados.
          </Text>
        </SimpleGrid>

        <Box mt={8} pt={6} borderTop="1px solid" borderColor="whiteAlpha.200">
          <Text fontSize="xs" color="whiteAlpha.600" textAlign="center" lineHeight="1.6">
            {founder.oab}
            <br />
            A Mendes Assessoria Jurídica atua em conformidade com o Código de Ética e Disciplina da OAB e com o Estatuto da Advocacia.
          </Text>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

